import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { fmtLbs, today, Modal, useForm } from '../components/helpers';

const EMPTY = { run_date: today(), item_type: 'sku', finished_good_id: '', bulk_part_id: '', units_produced: '', resin_id: '', lbs_per_unit: '', notes: '' };

async function getDefaultResinForSku(fgId) {
  const { data } = await supabase.from('sku_components').select('resin_id, qty_per_unit').eq('finished_good_id', fgId).eq('component_type', 'resin').limit(1);
  if (data && data.length > 0) return { resin_id: data[0].resin_id, lbs_per_unit: data[0].qty_per_unit };
  return null;
}

export default function ProductionRuns() {
  const [runs, setRuns] = useState([]);
  const [skus, setSkus] = useState([]);
  const [bulkParts, setBulkParts] = useState([]);
  const [resins, setResins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, set, setForm] = useForm(EMPTY);
  const [preview, setPreview] = useState([]);
  const [saving, setSaving] = useState(false);

  async function load() {
    const [{ data: r }, { data: fg }, { data: bp }, { data: raw }] = await Promise.all([
      supabase.from('production_runs').select('*, finished_goods(sku,name), bulk_parts(id,name)').order('run_date', { ascending: false }).order('created_at', { ascending: false }),
      supabase.from('finished_goods').select('id,sku,name,case_qty').filter('bulk_part_id', 'is', null).order('sku'),
      supabase.from('bulk_parts').select('id,name').order('name'),
      supabase.from('raw_materials').select('id,name,on_hand').order('name'),
    ]);
    setRuns((r || []).map(run => ({ ...run, _isBulk: !run.finished_good_id })));
    setSkus(fg || []);
    setBulkParts(bp || []);
    setResins(raw || []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  // ─── PREVIEW ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!form.units_produced) { setPreview([]); return; }
    const units = Number(form.units_produced);
    if (form.item_type === 'bulk') {
      const lines = [];
      if (form.resin_id && form.lbs_per_unit) {
        const resin = resins.find(r => r.id === form.resin_id);
        const lbsNeeded = units * Number(form.lbs_per_unit);
        lines.push({ label: (resin?.name || 'Resin') + ' (deducted)', needed: lbsNeeded, unit: 'lbs', onHand: resin ? Number(resin.on_hand) : 0, short: resin ? lbsNeeded > Number(resin.on_hand) : false });
      }
      lines.push({ label: 'Bulk units added to inventory', needed: units, unit: 'units', onHand: null, short: false });
      setPreview(lines);
      return;
    }
    if (!form.finished_good_id) { setPreview([]); return; }
    buildSkuPreview(form.finished_good_id, units, form.resin_id);
  }, [form.finished_good_id, form.bulk_part_id, form.units_produced, form.item_type, form.resin_id, form.lbs_per_unit]);

  async function onSkuChange(e) {
    const fgId = e.target.value;
    setForm(f => ({ ...f, finished_good_id: fgId, resin_id: '', lbs_per_unit: '' }));
    if (fgId) {
      const defaults = await getDefaultResinForSku(fgId);
      if (defaults) setForm(f => ({ ...f, finished_good_id: fgId, resin_id: defaults.resin_id, lbs_per_unit: String(defaults.lbs_per_unit) }));
    }
  }

  async function buildSkuPreview(fgId, units, resinOverride) {
    const sku = skus.find(s => s.id === fgId);
    const caseQty = Number(sku?.case_qty) || 1;
    const { data: comps } = await supabase.from('sku_components').select('*, raw_materials(name,on_hand), components(name,on_hand)').eq('finished_good_id', fgId);
    if (!comps || comps.length === 0) { setPreview([]); return; }
    setPreview(comps.map(c => {
      if (c.component_type === 'resin') {
        const needed = units * Number(c.qty_per_unit);
        const activeResin = resinOverride ? resins.find(r => r.id === resinOverride) : c.raw_materials;
        const label = (activeResin?.name || 'Resin') + (resinOverride && resinOverride !== c.resin_id ? ' (override)' : '');
        return { label, needed, unit: 'lbs', onHand: Number(activeResin?.on_hand || 0), short: needed > Number(activeResin?.on_hand || 0) };
      } else {
        const needed = c.per_case ? (units / caseQty) * Number(c.qty_per_unit) : units * Number(c.qty_per_unit);
        return { label: (c.components?.name || 'Component') + (c.per_case ? ' (per case)' : ''), needed, unit: 'units', onHand: Number(c.components?.on_hand || 0), short: needed > Number(c.components?.on_hand || 0) };
      }
    }));
  }

  // ─── CORE: Deduct inventory and write run_components record ────────────────
  // Returns array of { item_type, item_id, item_name, qty_consumed, unit }
  async function deductComponents(fgId, units, resinOverride) {
    const sku = skus.find(s => s.id === fgId);
    const caseQty = Number(sku?.case_qty) || 1;
    const { data: comps } = await supabase.from('sku_components').select('*, raw_materials(id,name,on_hand), components(id,name,on_hand)').eq('finished_good_id', fgId);
    const consumed = [];
    for (const c of (comps || [])) {
      if (c.component_type === 'resin') {
        const resinId = resinOverride || c.raw_materials?.id;
        if (!resinId) continue;
        const qty = units * Number(c.qty_per_unit);
        const { data: rd } = await supabase.from('raw_materials').select('on_hand,name').eq('id', resinId).single();
        if (rd) {
          await supabase.from('raw_materials').update({ on_hand: Math.max(0, Number(rd.on_hand) - qty), updated_at: today() }).eq('id', resinId);
          await supabase.from('inventory_adjustments').insert({ item_type: 'raw', item_id: resinId, item_name: rd.name, adjustment: -qty, reason: 'Production run' });
          consumed.push({ item_type: 'resin', item_id: resinId, item_name: rd.name, qty_consumed: qty, unit: 'lbs' });
        }
      } else if (c.component_type === 'component' && c.components) {
        const qty = c.per_case ? (units / caseQty) * Number(c.qty_per_unit) : units * Number(c.qty_per_unit);
        await supabase.from('components').update({ on_hand: Math.max(0, Number(c.components.on_hand) - qty), updated_at: today() }).eq('id', c.components.id);
        await supabase.from('inventory_adjustments').insert({ item_type: 'raw', item_id: c.components.id, item_name: c.components.name, adjustment: -qty, reason: 'Production run' });
        consumed.push({ item_type: 'component', item_id: c.components.id, item_name: c.components.name, qty_consumed: qty, unit: 'units' });
      }
    }
    return consumed;
  }

  // ─── CORE: Reverse using stored run_components — single source of truth ────
  async function reverseRunComponents(runId) {
    const { data: rc } = await supabase.from('production_run_components').select('*').eq('run_id', runId);
    for (const c of (rc || [])) {
      if (c.item_type === 'resin') {
        const { data: rd } = await supabase.from('raw_materials').select('on_hand,name').eq('id', c.item_id).single();
        if (rd) {
          await supabase.from('raw_materials').update({ on_hand: Number(rd.on_hand) + Number(c.qty_consumed), updated_at: today() }).eq('id', c.item_id);
          await supabase.from('inventory_adjustments').insert({ item_type: 'raw', item_id: c.item_id, item_name: c.item_name, adjustment: Number(c.qty_consumed), reason: 'Production run reversal' });
        }
      } else {
        const { data: cd } = await supabase.from('components').select('on_hand').eq('id', c.item_id).single();
        if (cd) {
          await supabase.from('components').update({ on_hand: Number(cd.on_hand) + Number(c.qty_consumed), updated_at: today() }).eq('id', c.item_id);
          await supabase.from('inventory_adjustments').insert({ item_type: 'raw', item_id: c.item_id, item_name: c.item_name, adjustment: Number(c.qty_consumed), reason: 'Production run reversal' });
        }
      }
    }
  }

  // ─── DELETE ────────────────────────────────────────────────────────────────
  async function deleteRun(run) {
    if (!window.confirm('Delete this production run and reverse all inventory changes?')) return;
    const units = Number(run.units_produced);
    try {
      if (run._isBulk) {
        // Reverse bulk parts
        if (run.bulk_part_id) {
          const { data: bp } = await supabase.from('bulk_parts').select('on_hand,name').eq('id', run.bulk_part_id).single();
          if (bp) {
            await supabase.from('bulk_parts').update({ on_hand: Math.max(0, Number(bp.on_hand) - units), updated_at: today() }).eq('id', run.bulk_part_id);
            await supabase.from('inventory_adjustments').insert({ item_type: 'finished', item_id: run.bulk_part_id, item_name: bp.name, adjustment: -units, reason: 'Production run deleted' });
          }
        }
        // Reverse resin using stored lbs_consumed
        if (run.resin_id && run.lbs_consumed) {
          const { data: rd } = await supabase.from('raw_materials').select('on_hand,name').eq('id', run.resin_id).single();
          if (rd) {
            await supabase.from('raw_materials').update({ on_hand: Number(rd.on_hand) + Number(run.lbs_consumed), updated_at: today() }).eq('id', run.resin_id);
            await supabase.from('inventory_adjustments').insert({ item_type: 'raw', item_id: run.resin_id, item_name: rd.name, adjustment: Number(run.lbs_consumed), reason: 'Production run deleted' });
          }
        }
      } else {
        // Reverse ALL components using production_run_components — single source of truth
        await reverseRunComponents(run.id);
        // Reverse finished goods
        if (run.finished_good_id) {
          const { data: fg } = await supabase.from('finished_goods').select('on_hand,sku,name').eq('id', run.finished_good_id).single();
          if (fg) {
            await supabase.from('finished_goods').update({ on_hand: Math.max(0, Number(fg.on_hand) - units) }).eq('id', run.finished_good_id);
            await supabase.from('inventory_adjustments').insert({ item_type: 'finished', item_id: run.finished_good_id, item_name: fg.sku + ' - ' + fg.name, adjustment: -units, reason: 'Production run deleted' });
          }
        }
      }
      // production_run_components rows cascade-delete with the run
      await supabase.from('production_runs').delete().eq('id', run.id);
      load();
    } catch (err) {
      alert('Error deleting run: ' + err.message);
    }
  }

  // ─── EDIT ──────────────────────────────────────────────────────────────────
  function openEdit(run) {
    setEditing(run);
    setForm({ run_date: run.run_date, item_type: run._isBulk ? 'bulk' : 'sku', finished_good_id: run.finished_good_id || '', bulk_part_id: run.bulk_part_id || '', units_produced: String(run.units_produced), resin_id: run.resin_id || '', lbs_per_unit: run.lbs_per_unit || '', notes: run.notes || '' });
    setPreview([]);
    setModal(true);
  }

  // ─── SAVE ──────────────────────────────────────────────────────────────────
  async function save() {
    const units = Number(form.units_produced);
    if (!units) { alert('Enter units produced.'); return; }
    const isBulk = form.item_type === 'bulk';
    if (isBulk && !form.bulk_part_id && !editing) { alert('Select a bulk part.'); return; }
    if (!isBulk && !form.finished_good_id) { alert('Select a SKU.'); return; }
    const hasShortage = preview.some(p => p.short);
    if (hasShortage && !window.confirm('Some components are below what this run requires. Log anyway?')) return;
    setSaving(true);

    try {
      if (editing) {
        // EDIT — reverse original using run_components, then re-apply with new units
        const oldUnits = Number(editing.units_produced);
        const delta = units - oldUnits;

        if (!editing._isBulk && editing.finished_good_id && delta !== 0) {
          // Reverse original consumption exactly
          await reverseRunComponents(editing.id);
          // Re-apply with new unit count
          const consumed = await deductComponents(editing.finished_good_id, units, editing.resin_id || null);
          // Update stored run_components with new amounts
          await supabase.from('production_run_components').delete().eq('run_id', editing.id);
          if (consumed.length > 0) {
            await supabase.from('production_run_components').insert(consumed.map(c => ({ ...c, run_id: editing.id })));
          }
          // Adjust finished goods by delta
          const { data: fg } = await supabase.from('finished_goods').select('on_hand,sku,name').eq('id', editing.finished_good_id).single();
          if (fg) {
            await supabase.from('finished_goods').update({ on_hand: Math.max(0, Number(fg.on_hand) + delta) }).eq('id', editing.finished_good_id);
            await supabase.from('inventory_adjustments').insert({ item_type: 'finished', item_id: editing.finished_good_id, item_name: fg.sku + ' - ' + fg.name, adjustment: delta, reason: 'Production run edited (' + (delta > 0 ? '+' : '') + delta + ' units)' });
          }
        }
        // Update the run record
        const newLbsConsumed = units * Number(editing.lbs_per_unit || 0);
        await supabase.from('production_runs').update({ run_date: form.run_date, units_produced: units, lbs_consumed: newLbsConsumed, notes: form.notes.trim() || null }).eq('id', editing.id);

      } else if (isBulk) {
        // NEW BULK RUN
        const bulk = bulkParts.find(b => b.id === form.bulk_part_id);
        const lbsConsumed = form.resin_id && form.lbs_per_unit ? units * Number(form.lbs_per_unit) : 0;
        const { data: newRun } = await supabase.from('production_runs').insert({ run_date: form.run_date, finished_good_id: null, bulk_part_id: form.bulk_part_id, sku: bulk?.name || 'Bulk Part', units_produced: units, resin_id: form.resin_id || null, lbs_per_unit: Number(form.lbs_per_unit) || 0, lbs_consumed: lbsConsumed, notes: form.notes.trim() || null }).select().single();
        if (form.resin_id && lbsConsumed > 0) {
          const resin = resins.find(r => r.id === form.resin_id);
          if (resin) {
            await supabase.from('raw_materials').update({ on_hand: Math.max(0, Number(resin.on_hand) - lbsConsumed), updated_at: today() }).eq('id', resin.id);
            await supabase.from('inventory_adjustments').insert({ item_type: 'raw', item_id: resin.id, item_name: resin.name, adjustment: -lbsConsumed, reason: 'Production run: ' + units + ' units of ' + bulk?.name });
          }
        }
        const { data: bpData } = await supabase.from('bulk_parts').select('on_hand').eq('id', form.bulk_part_id).single();
        await supabase.from('bulk_parts').update({ on_hand: Number(bpData.on_hand) + units, updated_at: today() }).eq('id', form.bulk_part_id);
        await supabase.from('inventory_adjustments').insert({ item_type: 'finished', item_id: form.bulk_part_id, item_name: bulk?.name || 'Bulk Part', adjustment: units, reason: 'Production run: ' + form.run_date });

      } else {
        // NEW DIRECT SKU RUN
        const sku = skus.find(s => s.id === form.finished_good_id);
        const { data: comps } = await supabase.from('sku_components').select('*').eq('finished_good_id', form.finished_good_id);
        const resinComp = comps?.find(c => c.component_type === 'resin');
        const activeResinId = form.resin_id || resinComp?.resin_id || null;
        const lbsPerUnit = resinComp ? Number(resinComp.qty_per_unit) : 0;

        // Insert run record first to get the ID
        const { data: newRun } = await supabase.from('production_runs').insert({ run_date: form.run_date, finished_good_id: form.finished_good_id, bulk_part_id: null, sku: sku?.sku || '', units_produced: units, resin_id: activeResinId, lbs_per_unit: lbsPerUnit, lbs_consumed: lbsPerUnit * units, notes: form.notes.trim() || null }).select().single();

        // Deduct all components and capture exact consumption
        const consumed = await deductComponents(form.finished_good_id, units, form.resin_id || null);

        // Write production_run_components — the permanent record of what was consumed
        if (newRun && consumed.length > 0) {
          await supabase.from('production_run_components').insert(consumed.map(c => ({ ...c, run_id: newRun.id })));
        }

        // Increment finished goods
        const { data: fg } = await supabase.from('finished_goods').select('on_hand').eq('id', form.finished_good_id).single();
        await supabase.from('finished_goods').update({ on_hand: Number(fg.on_hand) + units }).eq('id', form.finished_good_id);
        await supabase.from('inventory_adjustments').insert({ item_type: 'finished', item_id: form.finished_good_id, item_name: sku?.sku + ' - ' + sku?.name, adjustment: units, reason: 'Production run: ' + form.run_date });
      }
    } catch (err) {
      alert('Error saving run: ' + err.message);
    }

    setSaving(false); setModal(false); setEditing(null); setPreview([]); load();
  }

  // ─── RENDER ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Production Runs</div>
          <div className="page-sub">Trays → Finished Goods · PBC/RC → Bulk Parts · Edit/delete reverses exact amounts logged</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setForm(EMPTY); setEditing(null); setPreview([]); setModal(true); }}>+ Log Run</button>
      </div>

      <div className="tbl-wrap">
        <table>
          <thead><tr><th>Date</th><th>Output</th><th>Product</th><th>Units</th><th>Resin Consumed</th><th>Notes</th><th>Actions</th></tr></thead>
          <tbody>
            {loading && <tr className="empty-row"><td colSpan={7}>Loading...</td></tr>}
            {!loading && runs.length === 0 && <tr className="empty-row"><td colSpan={7}>No production runs logged yet.</td></tr>}
            {runs.map(r => (
              <tr key={r.id}>
                <td>{r.run_date}</td>
                <td>{r._isBulk ? <span className="badge badge-warn">Bulk</span> : <span className="badge badge-blue mono">{r.sku}</span>}</td>
                <td>{r._isBulk ? (r.bulk_parts?.name || r.sku) : (r.finished_goods?.name || r.sku)}</td>
                <td className="mono">{Number(r.units_produced).toLocaleString()}</td>
                <td className="mono">{r.lbs_consumed ? fmtLbs(r.lbs_consumed) : '—'}</td>
                <td className="text-muted" style={{ fontSize: 12 }}>{r.notes || '—'}</td>
                <td>
                  <div className="flex-row">
                    <button className="btn btn-sm" onClick={() => openEdit(r)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => deleteRun(r)}>Del</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={editing ? 'Edit Production Run' : 'Log Production Run'} onClose={() => { setModal(false); setEditing(null); setPreview([]); }} onSave={save} saveLabel={saving ? 'Saving...' : editing ? 'Save Changes' : 'Log Run'}>
          <div className="form-group">
            <label className="form-label">Run Date</label>
            <input className="form-input" type="date" value={form.run_date} onChange={set('run_date')} />
          </div>

          {!editing && (
            <div className="form-group">
              <label className="form-label">What are you running?</label>
              <div className="flex-row" style={{ marginBottom: 10 }}>
                <button className={'btn ' + (form.item_type === 'sku' ? 'btn-primary' : '')} onClick={() => setForm(f => ({ ...f, item_type: 'sku', bulk_part_id: '', resin_id: '', lbs_per_unit: '' }))}>Tray / Direct SKU</button>
                <button className={'btn ' + (form.item_type === 'bulk' ? 'btn-primary' : '')} onClick={() => setForm(f => ({ ...f, item_type: 'bulk', finished_good_id: '' }))}>PBC / RC (Bulk Part)</button>
              </div>
            </div>
          )}

          {form.item_type === 'sku' && !editing && (
            <div className="form-group">
              <label className="form-label">SKU *</label>
              <select className="form-input" value={form.finished_good_id} onChange={onSkuChange}>
                <option value="">— Select SKU —</option>
                {skus.map(s => <option key={s.id} value={s.id}>{s.sku} – {s.name}</option>)}
              </select>
            </div>
          )}

          {form.item_type === 'sku' && !editing && form.finished_good_id && (
            <div className="form-group">
              <label className="form-label">Resin</label>
              <select className="form-input" value={form.resin_id} onChange={set('resin_id')}>
                <option value="">— Select resin —</option>
                {resins.map(r => <option key={r.id} value={r.id}>{r.name} ({Number(r.on_hand).toLocaleString()} lbs on hand)</option>)}
              </select>
              <div className="form-hint">Pre-filled from SKU default — change to override for this run</div>
            </div>
          )}

          {form.item_type === 'bulk' && !editing && (
            <>
              <div className="form-group">
                <label className="form-label">Bulk Part *</label>
                <select className="form-input" value={form.bulk_part_id} onChange={set('bulk_part_id')}>
                  <option value="">— Select bulk part —</option>
                  {bulkParts.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Resin Used</label>
                  <select className="form-input" value={form.resin_id} onChange={set('resin_id')}>
                    <option value="">— Select resin —</option>
                    {resins.map(r => <option key={r.id} value={r.id}>{r.name} ({Number(r.on_hand).toLocaleString()} lbs)</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Lbs per Unit</label>
                  <input className="form-input mono" type="number" min="0" step="0.001" value={form.lbs_per_unit} onChange={set('lbs_per_unit')} placeholder="0.000" />
                </div>
              </div>
            </>
          )}

          {editing && (
            <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--muted)' }}>
              Editing: <strong style={{ color: 'var(--text)' }}>{editing.sku}</strong> · Original: <strong style={{ color: 'var(--text)' }}>{Number(editing.units_produced).toLocaleString()} units</strong>
              <div style={{ marginTop: 4 }}>Original consumption will be reversed exactly, then re-applied with the new quantity.</div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Units Produced *</label>
            <input className="form-input mono" type="number" min="1" value={form.units_produced} onChange={set('units_produced')} placeholder="0" autoFocus />
          </div>

          {preview.length > 0 && !editing && (
            <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>Preview</div>
              {preview.map((p, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, marginBottom: 4 }}>
                  <span>{p.label}</span>
                  <span>
                    <span style={{ fontFamily: 'var(--mono)', fontWeight: 600, color: p.short ? 'var(--danger)' : 'inherit' }}>
                      {p.unit === 'lbs' ? p.needed.toLocaleString(undefined, { maximumFractionDigits: 1 }) + ' lbs' : Number(p.needed).toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' units'}
                    </span>
                    {p.onHand !== null && <span style={{ color: 'var(--muted)', fontSize: 11, marginLeft: 6 }}>({p.onHand.toLocaleString()} on hand{p.short ? ' ⚠' : ''})</span>}
                  </span>
                </div>
              ))}
            </div>
          )}

          {editing && form.units_produced && Number(form.units_produced) !== Number(editing.units_produced) && (
            <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>Net change</div>
              <span style={{ fontFamily: 'var(--mono)', fontWeight: 600, color: Number(form.units_produced) > Number(editing.units_produced) ? 'var(--success)' : 'var(--danger)' }}>
                {Number(form.units_produced) > Number(editing.units_produced) ? '+' : ''}{Number(form.units_produced) - Number(editing.units_produced)} units
              </span>
              <span style={{ color: 'var(--muted)', fontSize: 12, marginLeft: 8 }}>Original amounts reversed, new amounts applied</span>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Notes (optional)</label>
            <input className="form-input" value={form.notes} onChange={set('notes')} placeholder="e.g. Mold #2, lot number, shift..." />
          </div>
        </Modal>
      )}
    </div>
  );
}